/**
 * Auto → Light → Dark, as one small chip beside the offline badge.
 *
 * A cycling button rather than a switch, because the useful third state is
 * "follow the phone" and a two-position switch cannot say that. The label
 * spells out where you are instead of leaving it to an icon — this app writes
 * "GRADE IT LATER" on a button, so a sun glyph would be the odd one out.
 */

import { useEffect, useState } from "react";

import {
  applyTheme,
  loadTheme,
  onSystemThemeChange,
  resolveTheme,
  saveTheme,
  THEMES,
  type Theme,
} from "../lib/theme";

const LABELS: Record<Theme, string> = {
  auto: "Auto",
  light: "Light",
  dark: "Dark",
};

export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(loadTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // On "auto" the palette follows a media query with no help from us, but the
  // browser chrome colour is set imperatively and would be left stale.
  useEffect(() => {
    if (theme !== "auto") return;
    return onSystemThemeChange(() => applyTheme("auto"));
  }, [theme]);

  const next = THEMES[(THEMES.indexOf(theme) + 1) % THEMES.length]!;

  return (
    <button
      type="button"
      onClick={() => {
        setTheme(next);
        // Persisted here rather than in the effect, so that applying a theme
        // and choosing one stay separate things.
        saveTheme(next);
      }}
      title={`Theme: ${LABELS[theme]}${theme === "auto" ? ` (${resolveTheme(theme)})` : ""}. Tap for ${LABELS[next]}.`}
      aria-label={`Colour theme: ${LABELS[theme]}. Change to ${LABELS[next]}.`}
      className="no-print inline-flex cursor-pointer items-center gap-1.5 rounded-[2px] border border-graphite-2/40 bg-graphite/5 px-2 py-0.5 font-display text-[12px] font-semibold uppercase tracking-[0.08em] text-graphite-2"
    >
      <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-graphite-2/60" />
      {LABELS[theme]}
    </button>
  );
}
